// flush.js -- single-flight, owner-bound attempt-ledger flush.
// Spec: docs/specs/2026-09-29-attempt-ledger.md §3 decision 7, §4 steps 6-8, §6b, §10.
// Single job: deliver an owner's finished attempt entries to /api/attempt-log.
//   One getSession() snapshot per run; abort unless snapshot.user.id === uid. Re-check the CURRENT
//   session user before each request and stop if it changed. Every request carries the SNAPSHOT
//   token via apiFetch -- never authedApiFetch, which re-reads the session at send time (api.js:29)
//   and could post A's entry with B's token.
//   204 -> settle. 400 -> reject (L4) + one captureMessage per app session. Anything else -> keep, stop.
//   No Sentry for 5xx/network (§10: it would feed the loop). Never throws into callers.
import * as Sentry from '../errorReporting';
import { supabase } from '../supabase';
import { apiFetch } from '../api';
import * as buffer from './buffer';

let inFlight = null;
let rerunUid = null;
let warned400 = false;

async function sessionNow() {
  try {
    const { data } = await supabase.auth.getSession();
    return data?.session || null;
  } catch {
    return null; // transient: the next trigger retries
  }
}

async function runOnce(uid) {
  if (!supabase) return;
  const entries = buffer.loadForFlush(uid, Date.now());
  if (entries.length === 0) return;
  const snapshot = await sessionNow();
  if (!snapshot?.access_token || snapshot.user?.id !== uid) return;
  const token = snapshot.access_token;
  for (const entry of entries) {
    const current = await sessionNow();
    if (current?.user?.id !== uid) return;
    buffer.claimLoss(entry.attempt_id);
    buffer.markSent(entry.attempt_id);
    const live = buffer.loadForFlush(uid, Date.now()).find((e) => e.attempt_id === entry.attempt_id);
    if (!live) continue;
    let res;
    buffer.markInFlight(entry.attempt_id); // L1 must not evict it while the POST is out (Ultron P2-1)
    try {
      res = await apiFetch('/api/attempt-log', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(buffer.toRequestBody(live)),
      });
    } catch {
      return; // network: keep the entry with its claim (§6b.5); no Sentry by design (§10)
    } finally {
      buffer.clearInFlight(entry.attempt_id);
    }
    if (res?.status === 204) {
      buffer.settle(entry.attempt_id);
    } else if (res?.status === 400) {
      buffer.reject(entry.attempt_id);
      if (!warned400) {
        warned400 = true;
        Sentry.captureMessage('Attempt ledger: entry rejected by /api/attempt-log (400), dropped', 'warning');
      }
    } else {
      return; // 401 / 429 / 5xx: keep, retry on the next trigger
    }
  }
}

export function flushOwner(uid) {
  if (!uid) return Promise.resolve();
  if (inFlight) { rerunUid = uid; return inFlight; }
  inFlight = (async () => {
    try {
      let next = uid;
      while (next) {
        rerunUid = null;
        await runOnce(next);
        next = rerunUid;
      }
    } catch (err) {
      // A bug in the ledger itself (not a delivery failure): report it, never throw into the sync path.
      Sentry.captureException(err, { tags: { function: 'attempt-ledger-flush' } });
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}

export function __resetFlushForTests() { inFlight = null; rerunUid = null; warned400 = false; }
